import React, { useEffect, useMemo, useState } from 'react';
import { ensureAreaOption, ensureOption, formatStage } from '../data/team';
import { ClientConsolidationAPI } from '../services/clientConsolidation';
import { CustomersAPI } from '../services/customers';

function todayPlus(days) {
  const date = new Date();
  date.setDate(date.getDate() + days);
  return date.toISOString().slice(0, 10);
}

function pickFirst(list, fallback) {
  return Array.isArray(list) && list.length ? list[0] : fallback;
}

function normalizeMasterClientOption(client = {}) {
  return {
    _id: String(client.id || client._id || ''),
    name: client.name || client.corporateName || client.fantasyName || '',
    document: client.documentNumber || client.document || '',
    email: client.email || '',
    phone: client.phone || '',
    contactName: client.fantasyName || client.corporateName || '',
    address: [client.city, client.state].filter(Boolean).join(' - '),
    source: 'master',
    linkedOrigins: client.linkedOrigins || [],
    linkCount: client.linkCount || 0
  };
}

function initialForm(currentUser, workflowOptions, initialData = {}) {
  const customer = initialData.customer || {};
  const assignee = initialData.assignee || initialData.assignedTo || {};
  const assigneeId = typeof assignee === 'string' ? assignee : assignee?.id;
  const assigneeName = typeof assignee === 'string' ? assignee : assignee?.name;

  return {
    title: initialData.title || '',
    description: initialData.description || '',
    customerSearch: customer.name || initialData.customerName || '',
    customerId: customer.id || customer._id || initialData.customerId || '',
    customerName: customer.name || initialData.customerName || '',
    customerDocument: customer.document || '',
    customerEmail: customer.email || '',
    customerPhone: customer.phone || '',
    customerContactName: customer.contactName || '',
    customerAddress: customer.address || '',
    customerSource: customer.source || (customer.id || initialData.customerId ? 'master' : ''),
    stage: initialData.stage || pickFirst(workflowOptions?.stages, 'Entrada'),
    status: initialData.status || pickFirst(workflowOptions?.statuses, 'Aberta'),
    priority: initialData.priority || pickFirst(workflowOptions?.priorities, 'Media'),
    areaCode: initialData.area?.code || '',
    dueDate: initialData.dueDate || todayPlus(2),
    assigneeId: assigneeId || currentUser?.id || '',
    assigneeName: assigneeName || currentUser?.name || ''
  };
}

function customerPayloadFromForm(form, selectedCustomer) {
  if (selectedCustomer) {
    return {
      id: selectedCustomer._id,
      name: selectedCustomer.name,
      document: selectedCustomer.document,
      email: selectedCustomer.email,
      phone: selectedCustomer.phone,
      contactName: selectedCustomer.contactName,
      address: selectedCustomer.address,
      source: selectedCustomer.source
    };
  }

  if (!form.customerId) return undefined;

  return {
    id: form.customerId,
    name: form.customerName,
    document: form.customerDocument,
    email: form.customerEmail,
    phone: form.customerPhone,
    contactName: form.customerContactName,
    address: form.customerAddress,
    source: form.customerSource || 'master'
  };
}

export default function TaskCreateModal({
  currentUser,
  workflowOptions = {},
  users = [],
  initialData = null,
  title = 'Cadastrar demanda operacional',
  description = 'Cadastre a demanda, defina a área, o responsável e o prazo inicial.',
  submitLabel = 'Criar tarefa operacional',
  loading = false,
  onCreate,
  onClose
}) {
  const normalizedInitialData = useMemo(() => initialData || {}, [initialData]);
  const stageOptions = useMemo(() => ensureOption(workflowOptions.stages, ''), [workflowOptions]);
  const statusOptions = useMemo(() => ensureOption(workflowOptions.statuses, ''), [workflowOptions]);
  const priorityOptions = useMemo(() => ensureOption(workflowOptions.priorities, ''), [workflowOptions]);
  const areaOptions = useMemo(() => ensureAreaOption(workflowOptions.areas, normalizedInitialData.area), [workflowOptions, normalizedInitialData]);
  const userOptions = useMemo(() => {
    const list = [...users];
    if (currentUser?.id && !list.some((user) => user.id === currentUser.id)) list.unshift(currentUser);
    if (normalizedInitialData.assignee?.id && !list.some((user) => user.id === normalizedInitialData.assignee.id)) list.push(normalizedInitialData.assignee);
    return list;
  }, [currentUser, normalizedInitialData, users]);
  const [form, setForm] = useState(() => initialForm(currentUser, workflowOptions, normalizedInitialData));
  const [customerOptions, setCustomerOptions] = useState([]);
  const [customerLoading, setCustomerLoading] = useState(false);
  const [useManualCustomer, setUseManualCustomer] = useState(false);

  useEffect(() => {
    setForm((current) => ({
      ...current,
      stage: stageOptions.includes(current.stage) ? current.stage : pickFirst(stageOptions, 'Entrada'),
      status: statusOptions.includes(current.status) ? current.status : pickFirst(statusOptions, 'Aberta'),
      priority: priorityOptions.includes(current.priority) ? current.priority : pickFirst(priorityOptions, 'Media'),
      areaCode: areaOptions.some((area) => area.code === current.areaCode) ? current.areaCode : '',
      assigneeId: current.assigneeId || currentUser?.id || '',
      assigneeName: current.assigneeName || currentUser?.name || ''
    }));
  }, [areaOptions, currentUser, priorityOptions, stageOptions, statusOptions]);

  useEffect(() => {
    setForm(initialForm(currentUser, workflowOptions, normalizedInitialData));
    setUseManualCustomer(false);
    setCustomerOptions([]);
  }, [currentUser, normalizedInitialData, workflowOptions]);

  useEffect(() => {
    if (useManualCustomer) return;

    const query = form.customerSearch?.trim();
    const isCurrentSelection = form.customerId && query && query === form.customerName?.trim();
    if (isCurrentSelection) {
      setCustomerOptions([]);
      return;
    }

    if (!query || query.length < 2) {
      setCustomerOptions([]);
      return;
    }

    const timeout = setTimeout(async () => {
      setCustomerLoading(true);
      try {
        const payload = await ClientConsolidationAPI.masters({ q: query, limit: 8 });
        setCustomerOptions((payload.rows || []).map(normalizeMasterClientOption));
      } catch (_error) {
        setCustomerOptions([]);
      } finally {
        setCustomerLoading(false);
      }
    }, 250);

    return () => clearTimeout(timeout);
  }, [form.customerId, form.customerName, form.customerSearch, useManualCustomer]);

  const handleCustomerSelect = (customer) => {
    setUseManualCustomer(false);
    setCustomerOptions([]);
    setForm((current) => ({
      ...current,
      customerSearch: customer.name,
      customerId: customer._id,
      customerName: customer.name || '',
      customerDocument: customer.document || '',
      customerEmail: customer.email || '',
      customerPhone: customer.phone || '',
      customerContactName: customer.contactName || '',
      customerAddress: customer.address || '',
      customerSource: customer.source || 'master'
    }));
  };

  const handleEnableManualCustomer = () => {
    setUseManualCustomer(true);
    setCustomerOptions([]);
    setForm((current) => ({
      ...current,
      customerId: '',
      customerName: current.customerSearch || current.customerName || '',
      customerDocument: '',
      customerEmail: '',
      customerPhone: '',
      customerContactName: '',
      customerAddress: '',
      customerSource: ''
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!form.title.trim() || !onCreate) return;

    let selectedCustomer = null;
    if (useManualCustomer && form.customerName.trim()) {
      selectedCustomer = await CustomersAPI.create({
        name: form.customerName.trim(),
        document: form.customerDocument.trim(),
        email: form.customerEmail.trim(),
        phone: form.customerPhone.trim(),
        contactName: form.customerContactName.trim(),
        address: form.customerAddress.trim(),
        source: 'manual'
      });
    }

    const payload = {
      title: form.title.trim(),
      description: form.description.trim(),
      customer: customerPayloadFromForm(form, selectedCustomer),
      assignee: form.assigneeId ? { id: form.assigneeId, name: form.assigneeName || form.assigneeId } : null,
      stage: form.stage,
      status: form.status,
      priority: form.priority,
      area: form.areaCode ? { code: form.areaCode } : null,
      dueDate: form.dueDate || undefined
    };

    await onCreate(payload);
  };

  return (
    <div className="drawer-backdrop">
      <aside className="task-create-modal shadow-lg">
        <div className="d-flex justify-content-between align-items-start gap-3">
          <div>
            <div className="eyebrow">Nova tarefa</div>
            <h2>{title}</h2>
            <p className="text-muted mb-0">{description}</p>
          </div>
          <button className="btn btn-outline-secondary" type="button" onClick={onClose}>Fechar</button>
        </div>

        <form className="d-grid gap-3 mt-4" onSubmit={handleSubmit}>
          <div>
            <label className="form-label" htmlFor="task-create-title">Título da demanda</label>
            <input id="task-create-title" className="form-control" value={form.title} onChange={(event) => setForm((current) => ({ ...current, title: event.target.value }))} placeholder="Ex.: Instalação no cliente XPTO" />
          </div>

          <div>
            <label className="form-label" htmlFor="task-create-customer">Cliente</label>
            {!useManualCustomer && (
              <>
                <div className="d-flex gap-2">
                  <input
                    id="task-create-customer"
                    className="form-control"
                    value={form.customerSearch}
                    onChange={(event) => setForm((current) => ({
                      ...current,
                      customerSearch: event.target.value,
                      customerId: '',
                      customerName: '',
                      customerDocument: '',
                      customerEmail: '',
                      customerPhone: '',
                      customerContactName: '',
                      customerAddress: '',
                      customerSource: ''
                    }))}
                    placeholder="Buscar Cliente Mestre"
                  />
                  <button type="button" className="btn btn-outline-primary" onClick={() => { window.location.href = '/sync-center'; }} disabled={customerLoading}>
                    Central
                  </button>
                </div>
                <div className="form-text">Busque o Cliente Mestre consolidado. Ele reúne os cadastros vinculados do Bling e do AUVO.</div>
                {!!customerOptions.length && (
                  <div className="customer-search-results mt-2">
                    {customerOptions.map((customer) => (
                      <button
                        key={customer._id}
                        type="button"
                        className="customer-result-item"
                        onClick={() => handleCustomerSelect(customer)}
                      >
                        <strong>{customer.name}</strong>
                        <span>
                          {customer.document || customer.email || customer.phone || 'Sem contato adicional'}
                          {customer.linkedOrigins?.length ? ` · Origens: ${customer.linkedOrigins.join(', ')}` : ''}
                        </span>
                      </button>
                    ))}
                  </div>
                )}
                {!!form.customerId && (
                  <div className="selected-customer-card mt-2">
                    <strong>{form.customerName}</strong>
                    <div className="small text-muted">{form.customerDocument || form.customerEmail || form.customerPhone || 'Sem contato complementar'} · Cliente Mestre</div>
                  </div>
                )}
              </>
            )}
            <div className="d-flex gap-2 mt-2 flex-wrap">
              <button type="button" className="btn btn-sm btn-outline-dark" onClick={handleEnableManualCustomer}>
                Cliente ou contato não cadastrado
              </button>
              {useManualCustomer && (
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary"
                  onClick={() => {
                    setUseManualCustomer(false);
                    setForm((current) => ({
                      ...current,
                      customerSearch: '',
                      customerId: '',
                      customerName: '',
                      customerDocument: '',
                      customerEmail: '',
                      customerPhone: '',
                      customerContactName: '',
                      customerAddress: '',
                      customerSource: ''
                    }));
                  }}
                >
                  Voltar para busca
                </button>
              )}
            </div>
          </div>

          {useManualCustomer && (
            <div className="row g-3">
              <div className="col-md-6">
                <label className="form-label" htmlFor="task-create-customer-name">Nome do cliente ou empresa</label>
                <input id="task-create-customer-name" className="form-control" value={form.customerName} onChange={(event) => setForm((current) => ({ ...current, customerName: event.target.value }))} />
              </div>
              <div className="col-md-6">
                <label className="form-label" htmlFor="task-create-customer-contact">Pessoa de contato</label>
                <input id="task-create-customer-contact" className="form-control" value={form.customerContactName} onChange={(event) => setForm((current) => ({ ...current, customerContactName: event.target.value }))} />
              </div>
              <div className="col-md-4">
                <label className="form-label" htmlFor="task-create-customer-phone">Telefone</label>
                <input id="task-create-customer-phone" className="form-control" value={form.customerPhone} onChange={(event) => setForm((current) => ({ ...current, customerPhone: event.target.value }))} />
              </div>
              <div className="col-md-4">
                <label className="form-label" htmlFor="task-create-customer-email">E-mail</label>
                <input id="task-create-customer-email" className="form-control" value={form.customerEmail} onChange={(event) => setForm((current) => ({ ...current, customerEmail: event.target.value }))} />
              </div>
              <div className="col-md-4">
                <label className="form-label" htmlFor="task-create-customer-document">Documento</label>
                <input id="task-create-customer-document" className="form-control" value={form.customerDocument} onChange={(event) => setForm((current) => ({ ...current, customerDocument: event.target.value }))} />
              </div>
              <div className="col-12">
                <label className="form-label" htmlFor="task-create-customer-address">Endereço</label>
                <input id="task-create-customer-address" className="form-control" value={form.customerAddress} onChange={(event) => setForm((current) => ({ ...current, customerAddress: event.target.value }))} />
              </div>
            </div>
          )}

          <div>
            <label className="form-label" htmlFor="task-create-description">Relato inicial</label>
            <textarea id="task-create-description" className="form-control" rows={3} value={form.description} onChange={(event) => setForm((current) => ({ ...current, description: event.target.value }))} placeholder="Descreva a demanda e o contexto inicial." />
          </div>

          <div className="row g-3">
            <div className="col-md-3">
              <label className="form-label" htmlFor="task-create-stage">Etapa inicial</label>
              <select id="task-create-stage" className="form-select" value={form.stage} onChange={(event) => setForm((current) => ({ ...current, stage: event.target.value }))}>
                {stageOptions.map((stage) => <option key={stage} value={stage}>{formatStage(stage)}</option>)}
              </select>
            </div>
            <div className="col-md-3">
              <label className="form-label" htmlFor="task-create-status">Status</label>
              <select id="task-create-status" className="form-select" value={form.status} onChange={(event) => setForm((current) => ({ ...current, status: event.target.value }))}>
                {statusOptions.map((status) => <option key={status} value={status}>{status}</option>)}
              </select>
            </div>
            <div className="col-md-3">
              <label className="form-label" htmlFor="task-create-priority">Prioridade</label>
              <select id="task-create-priority" className="form-select" value={form.priority} onChange={(event) => setForm((current) => ({ ...current, priority: event.target.value }))}>
                {priorityOptions.map((priority) => <option key={priority} value={priority}>{priority}</option>)}
              </select>
            </div>
            <div className="col-md-3">
              <label className="form-label" htmlFor="task-create-area">Área</label>
              <select id="task-create-area" className="form-select" value={form.areaCode} onChange={(event) => setForm((current) => ({ ...current, areaCode: event.target.value }))}>
                <option value="">Sem área</option>
                {areaOptions.map((area) => <option key={area.code} value={area.code}>{area.name}</option>)}
              </select>
            </div>
          </div>

          <div className="row g-3">
            <div className="col-md-6">
              <label className="form-label" htmlFor="task-create-assignee">Responsável do próximo passo</label>
              <select
                id="task-create-assignee"
                className="form-select"
                value={form.assigneeId}
                onChange={(event) => {
                  const selected = userOptions.find((user) => user.id === event.target.value);
                  setForm((current) => ({
                    ...current,
                    assigneeId: event.target.value,
                    assigneeName: selected?.name || current.assigneeName
                  }));
                }}
              >
                <option value="">Definir depois</option>
                {userOptions.map((user) => <option key={user.id} value={user.id}>{user.name}</option>)}
              </select>
            </div>
            <div className="col-md-6">
              <label className="form-label" htmlFor="task-create-due-date">Prazo</label>
              <input id="task-create-due-date" className="form-control" type="date" value={form.dueDate} onChange={(event) => setForm((current) => ({ ...current, dueDate: event.target.value }))} />
            </div>
          </div>

          {form.assigneeId && (
            <div>
              <label className="form-label" htmlFor="task-create-assignee-name">Nome do responsável</label>
              <input id="task-create-assignee-name" className="form-control" value={form.assigneeName} onChange={(event) => setForm((current) => ({ ...current, assigneeName: event.target.value }))} />
            </div>
          )}

          <div className="d-flex justify-content-end gap-2 pt-2">
            <button className="btn btn-outline-secondary" type="button" onClick={onClose}>Cancelar</button>
            <button className="btn btn-primary" type="submit" disabled={loading || !form.title.trim()}>{loading ? 'Criando...' : submitLabel}</button>
          </div>
        </form>
      </aside>
    </div>
  );
}
